import { useEffect, useRef, useState } from "react";
import "./booking.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { service: "", description: "", type: "", date: "", time: "", name: "", email: "", phone: "", privacy: false, marketing: false, website: "" };
const fmtDay = (d, o) => new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", ...o }).format(new Date(`${d}T12:00:00Z`));
const tzName = (d) => {
  const hour = +new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", hourCycle: "h23" }).format(new Date(`${d}T12:00:00Z`));
  return hour === 13 ? "BST" : "GMT";
};
const longWhen = (d, t) => `${fmtDay(d, { weekday: "long", day: "numeric", month: "long" })}, ${t} (UK time, ${tzName(d)})`;

function Step1({ v, set, services }) {
  return (
    <>
      <span className="kb-label">What do you need help with? <span className="kb-req">*</span></span>
      <div className="kb-chips" role="radiogroup">
        {services.map((s) => (
          <button type="button" key={s} role="radio" aria-checked={v.service === s} className={`kb-chip ${v.service === s ? "on" : ""}`}
            onClick={() => set({ service: s })} data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</button>
        ))}
      </div>
      <label className="kb-label" htmlFor="kb-desc">Briefly describe what you need <span className="kb-req">*</span></label>
      <textarea id="kb-desc" rows={4} maxLength={1500} value={v.description} onChange={(e) => set({ description: e.target.value })} data-testid="booking-description"
        placeholder="For example: I started self-employment this year and need help registering and filing my first return." />
      <p className="kb-hint">At least 10 characters. Please don't include UTRs, bank details or other confidential information.</p>
    </>
  );
}

function Step2({ v, set, slots, email }) {
  const days = slots?.days || [];
  const day = days.find((d) => d.date === v.date);
  if (slots && !slots.enabled) {
    return <div className="kb-note" data-testid="booking-closed">Online booking is not open at the moment. Please {email ? <>email <b>{email}</b></> : "use the enquiry form on our Contact page"} and we will arrange a time with you.</div>;
  }
  return (
    <>
      <span className="kb-label">How would you like to speak? <span className="kb-req">*</span></span>
      <div className="kb-types">
        <button type="button" className={`kb-type ${v.type === "telephone" ? "on" : ""}`} onClick={() => set({ type: "telephone" })} data-testid="booking-type-telephone">
          <b>☏ Telephone call</b><small>We call you at the agreed time</small></button>
        <button type="button" className={`kb-type ${v.type === "video" ? "on" : ""}`} onClick={() => set({ type: "video" })} data-testid="booking-type-video">
          <b>▶ Google Meet video call</b><small>Joining link emailed once confirmed</small></button>
      </div>
      <span className="kb-label">Choose a date <span className="kb-req">*</span></span>
      {!slots ? <p className="kb-hint">Loading available times…</p> : !days.length ? (
        <div className="kb-note" data-testid="booking-no-slots">There are no free times in the coming weeks. Please {email ? <>email <b>{email}</b></> : "use our Contact page"} and we will find a time.</div>
      ) : (
        <>
          <div className="kb-days" data-testid="booking-days">
            {days.map((d) => (
              <button type="button" key={d.date} className={`kb-day ${v.date === d.date ? "on" : ""}`} onClick={() => set({ date: d.date, time: "" })} data-testid={`booking-day-${d.date}`}>
                {fmtDay(d.date, { weekday: "short" })}<b>{fmtDay(d.date, { day: "numeric" })}</b>{fmtDay(d.date, { month: "short" })}
              </button>
            ))}
          </div>
          {day && (
            <>
              <span className="kb-label">Available times ({slots.slot_minutes} minutes) <span className="kb-req">*</span></span>
              <div className="kb-slots" data-testid="booking-slots">
                {day.slots.map((t) => <button type="button" key={t} className={`kb-slot ${v.time === t ? "on" : ""}`} onClick={() => set({ time: t })} data-testid={`booking-slot-${t.replace(":", "")}`}>{t}</button>)}
              </div>
              <p className="kb-tz">All times are UK time (Europe/London, {tzName(day.date)} on this date).</p>
            </>
          )}
        </>
      )}
      <div className="kb-note">Prefer to meet face to face? {email ? <>Email <b>{email}</b> and we'll arrange it directly.</> : "Please contact us by email and we'll arrange it directly."}</div>
    </>
  );
}

function Step3({ v, set }) {
  return (
    <>
      <div className="kb-summary" data-testid="booking-summary"><b>{v.service}</b> · {v.type === "telephone" ? "Telephone call" : "Google Meet video call"} · {longWhen(v.date, v.time)}</div>
      <div className="kb-grid">
        <div><label className="kb-label" htmlFor="kb-name">Full name <span className="kb-req">*</span></label>
          <input id="kb-name" type="text" autoComplete="name" value={v.name} onChange={(e) => set({ name: e.target.value })} data-testid="booking-name" /></div>
        <div><label className="kb-label" htmlFor="kb-email">Email address <span className="kb-req">*</span></label>
          <input id="kb-email" type="email" autoComplete="email" value={v.email} onChange={(e) => set({ email: e.target.value })} data-testid="booking-email" /></div>
        {v.type === "telephone" && (
          <div className="kb-full"><label className="kb-label" htmlFor="kb-phone">Telephone number for the call <span className="kb-req">*</span></label>
            <input id="kb-phone" type="tel" autoComplete="tel" value={v.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="booking-phone" />
            <p className="kb-hint">Only used to call you for this consultation.</p></div>
        )}
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="kt-hp" value={v.website} onChange={(e) => set({ website: e.target.value })} />
      <label className="kb-check"><input type="checkbox" checked={v.privacy} onChange={(e) => set({ privacy: e.target.checked })} data-testid="booking-privacy" />
        <span>I have read the <a href="/#page-privacy" target="_blank" rel="noopener noreferrer">privacy notice</a> and agree that KT Global Advisory may use these details to respond to my request. <span className="kb-req">*</span></span></label>
      <label className="kb-check"><input type="checkbox" checked={v.marketing} onChange={(e) => set({ marketing: e.target.checked })} data-testid="booking-marketing" />
        <span>Optional: send me occasional KT tax-deadline updates (unsubscribe anytime).</span></label>
    </>
  );
}

function valid(step, v) {
  if (step === 1) return v.service && v.description.trim().length >= 10;
  if (step === 2) return v.type && v.date && v.time;
  if (step === 3) return v.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email) && v.privacy && (v.type !== "telephone" || v.phone.replace(/\D/g, "").length >= 10);
  return true;
}

export function BookingModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [v, setV] = useState(EMPTY);
  const [slots, setSlots] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const opened = useRef(0);
  const closeBtn = useRef(null);
  const set = (p) => { setError(""); setV((x) => ({ ...x, ...p })); };

  const load = () => {
    setSlots(null);
    fetch(`${API}/public/booking/slots`).then((r) => r.json()).then(setSlots).catch(() => setSlots({ enabled: false, days: [] }));
    fetch(`${API}/public/site`).then((r) => r.json()).then((d) => setEmail(d?.business?.business_email || "")).catch(() => {});
  };
  useEffect(() => {
    const onOpen = () => { setOpen(true); setStep(1); setDone(null); setError(""); setV(EMPTY); opened.current = Date.now(); load(); };
    window.addEventListener("kt-open-booking", onOpen);
    return () => window.removeEventListener("kt-open-booking", onOpen);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtn.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [open]);
  if (!open) return null;

  const submit = async () => {
    setBusy(true); setError("");
    const wait = 3200 - (Date.now() - opened.current);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    try {
      const res = await fetch(`${API}/public/booking`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service: v.service, description: v.description, type: v.type, start_local: `${v.date}T${v.time}`, name: v.name, email: v.email,
          phone: v.type === "telephone" ? v.phone : "", privacy_ack: v.privacy, marketing_consent: v.marketing, website: v.website, elapsed_ms: Date.now() - opened.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && /time/.test(data.detail || "")) { load(); setStep(2); set({ time: "" }); }
        throw new Error(Array.isArray(data.detail) ? "Please check your details and try again." : data.detail || "Something went wrong. Please try again.");
      }
      setDone(data); setStep(4);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const next = () => (step === 3 ? submit() : step === 4 ? setOpen(false) : setStep(step + 1));
  const closed = step === 2 && slots && (!slots.enabled || !slots.days.length);
  const STEPS = ["Your enquiry", "Call type & time", "Your details"];
  return (
    <div className="kb-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }} data-testid="booking-overlay">
      <div className="kb-modal" role="dialog" aria-modal="true" aria-labelledby="kb-title" data-testid="booking-modal">
        <div className="kb-head">
          <div><div className="kb-eyebrow">Free initial consultation</div><h2 id="kb-title">Request a consultation</h2></div>
          <button type="button" ref={closeBtn} className="kb-close" aria-label="Close booking" onClick={() => setOpen(false)} data-testid="booking-close">×</button>
        </div>
        {step < 4 && <div className="kb-steps">{STEPS.map((s, i) => <span key={s} className={i < step ? "on" : ""}>{i + 1}. {s}</span>)}</div>}
        <div className="kb-body">
          {step === 1 && <Step1 v={v} set={set} services={slots?.services || ["General enquiry / not sure"]} />}
          {step === 2 && <Step2 v={v} set={set} slots={slots} email={email} />}
          {step === 3 && <Step3 v={v} set={set} />}
          {step === 4 && done && (
            <div className="kb-done" data-testid="booking-done">
              <div className="kb-tick">✓</div><span className="kb-pending">PENDING CONFIRMATION</span>
              <h3>Request received</h3>
              <p>We're holding <b>{longWhen(v.date, v.time)}</b> for you. <b>Your appointment is not yet confirmed.</b></p>
              <p>We'll review your request and email <b>{v.email}</b> to confirm{v.type === "video" ? ", including your Google Meet link" : ""}, usually within one working day.</p>
              {done.ref && <p className="kb-hint">Reference {done.ref}. An acknowledgement email is on its way.</p>}
            </div>
          )}
          {error && <div className="kb-err" role="alert" data-testid="booking-error">{error}</div>}
        </div>
        <div className="kb-foot">
          {step > 1 && step < 4 ? <button type="button" className="kb-btn light" onClick={() => setStep(step - 1)} data-testid="booking-back">Back</button> : <span />}
          {!closed && (
            <button type="button" className={`kb-btn ${step === 3 ? "gold" : "dark"}`} disabled={!valid(step, v) || busy} onClick={next} data-testid="booking-next">
              {busy ? "Sending…" : step === 3 ? "Send request" : step === 4 ? "Close" : "Continue"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
